/**
 * What the home page derives from its two reads — the catalog's Odoo projects
 * and the ArgoCD environment Applications — and from the pipelines read for
 * them, without asking for anything more.
 *
 * Plain functions, so the rules (which project lists which environments, what
 * is « à surveiller », the order of the projects table, the filters of « Mes
 * environnements », which projects get a CI status) are tested without
 * rendering anything.
 */

import type { StatusChipStatus } from '../ui';
import {
  healthChipStatus,
  syncChipStatus,
  toEnvironment,
  type ArgoApplication,
  type ProjectEnvironment,
} from '../projectEnvironments/argoApp';
import {
  branchActivity,
  compareBranches,
  isEphemeralBranch,
} from '../projectEnvironments/branchKind';
import {
  isPipelineRunning,
  type PipelineInfo,
} from '../projectEnvironments/pipelines';
import type { ProjectEntry } from '../projectEnvironments/projectList';

/**
 * One project of the home page: a row of « Projets », a group of « Mes
 * environnements ».
 */
export interface ProjectView {
  project: ProjectEntry;
  /**
   * By tier — prod, the staging branches, dev, then the ephemeral ones
   * (`compareBranches`).
   * Empty for a project without environment.
   */
  environments: ProjectEnvironment[];
}

/**
 * One card per CATALOG project, with the environments ArgoCD reports for it.
 *
 * The catalog decides which projects are listed: an Application whose project
 * the catalog does not show is left out (it is the policy that decides who
 * sees a project), and a project ArgoCD reports nothing for still gets its
 * card. Without `appsByProject` — ArgoCD not answered or failed — every
 * project is listed with no environment, and the page says why elsewhere.
 *
 * Environments without a usable branch name (`''`, or the `'?'` of
 * `toEnvironment`) are dropped, like the sidebar's `branchOptions` does: they
 * could only link to a branch page that cannot load.
 */
export function buildProjectViews(
  projects: ProjectEntry[],
  appsByProject: Map<string, ArgoApplication[]> | undefined,
): ProjectView[] {
  return projects.map(project => ({
    project,
    environments: (appsByProject?.get(project.projectId) ?? [])
      .map(toEnvironment)
      .filter(env => env.branch !== '' && env.branch !== '?')
      .sort((a, b) => compareBranches(a.branch, b.branch)),
  }));
}

/**
 * The ArgoCD health statuses that put an environment on the watch list:
 * ArgoCD's two failure states, and Unknown — a status nobody can vouch for.
 * Progressing (still settling) and Suspended (deliberate) stay off it.
 */
export const WATCHED_HEALTH = ['Degraded', 'Missing', 'Unknown'] as const;

export interface WatchItem {
  /** React key. */
  key: string;
  projectId: string;
  projectLabel: string;
  branch: string;
  /** The health status, when it is one of {@link WATCHED_HEALTH}. */
  health?: string;
  outOfSync: boolean;
  /** The branch's latest pipeline, when it failed. */
  failedPipeline?: PipelineInfo;
  /**
   * Days without a new deployment, for an ephemeral environment idle for
   * `INACTIVE_AFTER_DAYS` or more (branchKind.ts) — the « delete it? » reminder.
   */
  inactiveDays?: number;
  /** What the branch label's age is read from. */
  createdAt?: string;
  revisionDeployedAt?: string;
}

/**
 * Every environment with something wrong — health, sync, latest pipeline, or
 * an ephemeral environment left idle (`branchActivity`) — one entry per
 * environment however many reasons it has, in the order of the project cards.
 * `now` is what the idle time is counted to.
 */
export function deriveWatchList(
  views: ProjectView[],
  pipelinesByProject: Record<string, Record<string, PipelineInfo> | undefined>,
  now: number,
): WatchItem[] {
  const items: WatchItem[] = [];
  for (const { project, environments } of views) {
    for (const env of environments) {
      const health = (WATCHED_HEALTH as readonly string[]).includes(
        env.healthStatus,
      )
        ? env.healthStatus
        : undefined;
      const outOfSync = env.syncStatus === 'OutOfSync';
      const pipeline = pipelinesByProject[project.projectId]?.[env.branch];
      const failedPipeline =
        pipeline?.status === 'failed' ? pipeline : undefined;

      const activity = branchActivity(env.branch, env, now);
      const inactiveDays = activity.inactive ? activity.idleDays : undefined;

      if (health || outOfSync || failedPipeline || inactiveDays !== undefined) {
        items.push({
          key: `${project.entityRef}/${env.appName}`,
          projectId: project.projectId,
          projectLabel: project.label,
          branch: env.branch,
          health,
          outOfSync,
          failedPipeline,
          inactiveDays,
          createdAt: env.createdAt,
          revisionDeployedAt: env.revisionDeployedAt,
        });
      }
    }
  }
  return items;
}

/**
 * Whether a watched environment is SERIOUS: broken (a health of
 * {@link WATCHED_HEALTH} that `healthChipStatus` draws as failed, i.e.
 * Degraded or Missing) or with a failed latest pipeline. The others — out of
 * sync, an Unknown health, an idle ephemeral environment — are worth a look,
 * not an alarm: they tint the « À surveiller » banner amber rather than red.
 */
export function isSeriousWatchItem(item: WatchItem): boolean {
  return (
    (item.health !== undefined && healthChipStatus(item.health) === 'failed') ||
    item.failedPipeline !== undefined
  );
}

/**
 * The watch list in the order the banner shows it: the serious items first,
 * each group in the order of `deriveWatchList` (the projects').
 */
export function sortWatchItems(items: WatchItem[]): WatchItem[] {
  return [
    ...items.filter(isSeriousWatchItem),
    ...items.filter(item => !isSeriousWatchItem(item)),
  ];
}

/** The CI status of one project's branches. */
export type ProjectPipelines =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; byBranch: Record<string, PipelineInfo> };

/**
 * The branches of one project whose latest pipeline has not finished
 * (`isPipelineRunning`: running or pending), in the order of the branches
 * (`compareBranches`). Empty while the forge has not answered, when it
 * failed, and for a project whose CI is not read.
 */
export function runningPipelines(
  pipelines: ProjectPipelines | undefined,
): Array<{ branch: string; info: PipelineInfo }> {
  if (pipelines?.status !== 'ready') {
    return [];
  }
  return Object.entries(pipelines.byBranch)
    .filter(([, info]) => isPipelineRunning(info.status))
    .sort(([a], [b]) => compareBranches(a, b))
    .map(([branch, info]) => ({ branch, info }));
}

/** The figures of the header of « Projets » (ProjectsTable.tsx). */
export interface HomeFigures {
  projects: number;
  environments: number;
  ephemeral: number;
  /**
   * Branches with a pipeline running or pending, over the projects whose CI
   * is read; undefined while one of those reads is still going.
   */
  runningPipelines: number | undefined;
}

/**
 * The figures, from what the page already holds: the projects listed (the
 * catalog's projects), the environments ArgoCD reports for them and the
 * pipelines read for them.
 */
export function homeFigures(
  views: ProjectView[],
  pipelines: Record<string, ProjectPipelines>,
): HomeFigures {
  const environments = views.flatMap(view => view.environments);
  const states = Object.values(pipelines);
  return {
    projects: views.length,
    environments: environments.length,
    ephemeral: environments.filter(env => isEphemeralBranch(env.branch)).length,
    runningPipelines: states.some(state => state.status === 'loading')
      ? undefined
      : states.reduce(
          (total, state) => total + runningPipelines(state).length,
          0,
        ),
  };
}

/**
 * What is wrong with one environment, as a project row sums it up: its
 * health when it is not Healthy, else its sync when it is not Synced — a
 * broken environment is broken whatever its sync. `status` is the chip state
 * of `healthChipStatus` / `syncChipStatus`, `value` ArgoCD's word, verbatim.
 */
export interface EnvironmentProblem {
  branch: string;
  field: 'health' | 'sync';
  value: string;
  status: StatusChipStatus;
}

/** Undefined for an environment Healthy and Synced: nothing to say. */
export function environmentProblem(
  env: ProjectEnvironment,
): EnvironmentProblem | undefined {
  if (env.healthStatus !== 'Healthy') {
    return {
      branch: env.branch,
      field: 'health',
      value: env.healthStatus,
      status: healthChipStatus(env.healthStatus),
    };
  }
  if (env.syncStatus !== 'Synced') {
    return {
      branch: env.branch,
      field: 'sync',
      value: env.syncStatus,
      status: syncChipStatus(env.syncStatus),
    };
  }
  return undefined;
}

/** How bad a problem is, to pick the one a project row names. */
const PROBLEM_RANK: Record<StatusChipStatus, number> = {
  failed: 4,
  degraded: 3,
  unknown: 2,
  progressing: 1,
  healthy: 0,
};

/**
 * The state of a whole project, for its row's pill: `loading` while ArgoCD
 * has not answered, `unavailable` when it failed, `empty` without
 * environment, `healthy` when every environment is Healthy and Synced, and
 * otherwise the worst problem (the first of the branch order among equals)
 * with the number of the other environments that have one.
 */
export type ProjectState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'empty' }
  | { kind: 'healthy' }
  | { kind: 'problem'; worst: EnvironmentProblem; others: number };

export function projectState(
  environments: ProjectEnvironment[],
  environmentsState: 'loading' | 'error' | 'ready',
): ProjectState {
  if (environmentsState === 'loading') {
    return { kind: 'loading' };
  }
  if (environmentsState === 'error') {
    return { kind: 'unavailable' };
  }
  if (environments.length === 0) {
    return { kind: 'empty' };
  }
  const problems = environments
    .map(environmentProblem)
    .filter((problem): problem is EnvironmentProblem => problem !== undefined);
  if (problems.length === 0) {
    return { kind: 'healthy' };
  }
  const worst = problems.reduce((current, problem) =>
    PROBLEM_RANK[problem.status] > PROBLEM_RANK[current.status]
      ? problem
      : current,
  );
  return { kind: 'problem', worst, others: problems.length - 1 };
}

/**
 * The branch whose links a project row of « Projets » carries (Odoo, Grafana):
 * the `dev` environment, the one the `odoo-project` template creates with every
 * project (« dépôt + environnement dev »). None when ArgoCD lists no `dev`.
 */
export const DEFAULT_BRANCH = 'dev';

/** The backend's limit on refs per pipelines request (`MAX_REFS`). */
export const MAX_PIPELINE_REFS = 20;

/** How many projects get their CI status read on the home page. */
export const MAX_PIPELINE_PROJECTS = 10;

/** One request of the CI reads: one project, its first branches. */
export interface PipelineTarget {
  projectId: string;
  environments: ProjectEnvironment[];
}

/**
 * Which projects get a CI status, and for which branches: the first
 * `maxProjects` projects that have at least one environment with a known
 * repository, each with its first `maxRefs` such environments. One request
 * per project — never one for several, because the pipelines answer is keyed
 * by branch name and two projects both have a `dev`.
 */
export function pipelineTargets(
  views: ProjectView[],
  maxProjects = MAX_PIPELINE_PROJECTS,
  maxRefs = MAX_PIPELINE_REFS,
): PipelineTarget[] {
  return views
    .map(view => ({
      projectId: view.project.projectId,
      environments: view.environments
        .filter(env => Boolean(env.repoUrl))
        .slice(0, maxRefs),
    }))
    .filter(target => target.environments.length > 0)
    .slice(0, maxProjects);
}

/**
 * How a set of watch items reads: `danger` when one is serious
 * ({@link isSeriousWatchItem}), `warning` otherwise, undefined when there is
 * none — the tones the « À surveiller » banner took, now the stripe of a row
 * and the tone of the header's pill.
 */
export type WatchTone = 'danger' | 'warning';

export function watchTone(items: WatchItem[]): WatchTone | undefined {
  if (items.length === 0) {
    return undefined;
  }
  return items.some(isSeriousWatchItem) ? 'danger' : 'warning';
}

/** One project of « Projets », with what its order is decided on. */
export interface RankedProject {
  view: ProjectView;
  state: ProjectState;
  running: Array<{ branch: string; info: PipelineInfo }>;
  /** Its environments of the watch list, in the order of `deriveWatchList`. */
  watch: WatchItem[];
}

/**
 * The rank of a project in « Projets », lowest first: something serious to
 * watch (0), something else to watch (1), something under way — a pipeline
 * running, an environment Progressing — (2), the rest (3).
 */
export function projectRank(project: RankedProject): number {
  const tone = watchTone(project.watch);
  if (tone === 'danger') {
    return 0;
  }
  if (tone === 'warning') {
    return 1;
  }
  if (
    project.running.length > 0 ||
    (project.state.kind === 'problem' &&
      project.state.worst.status === 'progressing')
  ) {
    return 2;
  }
  return 3;
}

/**
 * The projects in the order of « Projets »: by {@link projectRank}, then by
 * name in `locale`. A new array.
 */
export function sortProjects<T extends RankedProject>(
  projects: T[],
  locale: string,
): T[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base' });
  return [...projects].sort(
    (a, b) =>
      projectRank(a) - projectRank(b) ||
      collator.compare(a.view.project.label, b.view.project.label),
  );
}

/**
 * The rows of « Projets », in their order: each project with its state
 * (`projectState`), its running pipelines and its watch items.
 */
export function rankProjects(
  views: ProjectView[],
  pipelines: Record<string, ProjectPipelines>,
  environmentsState: 'loading' | 'error' | 'ready',
  watchItems: WatchItem[],
  locale: string,
): RankedProject[] {
  const byProject = new Map<string, WatchItem[]>();
  for (const item of watchItems) {
    byProject.set(item.projectId, [
      ...(byProject.get(item.projectId) ?? []),
      item,
    ]);
  }
  return sortProjects(
    views.map(view => ({
      view,
      state: projectState(view.environments, environmentsState),
      running: runningPipelines(pipelines[view.project.projectId]),
      watch: byProject.get(view.project.projectId) ?? [],
    })),
    locale,
  );
}

/**
 * How many of the ArgoCD Applications read are on no row: `applications` (all
 * of them, the count the « Plateforme » card of mockup « L4 » showed) minus
 * the environments of the views — an Application of a project the catalog
 * does not list, without the project label, or without a branch the views
 * can read (`buildProjectViews`). Never negative.
 */
export function unlistedEnvironments(
  applications: number,
  views: ProjectView[],
): number {
  const listed = views.reduce(
    (total, view) => total + view.environments.length,
    0,
  );
  return Math.max(applications - listed, 0);
}

/** When a pipeline last moved, in epoch milliseconds; undefined if unknown. */
function pipelineTime(info: PipelineInfo): number | undefined {
  const time = Date.parse(info.updatedAt ?? info.startedAt ?? '');
  return Number.isNaN(time) ? undefined : time;
}

/**
 * The pipeline the « Dernier pipeline » column of « Projets » shows for a
 * whole project, out of the latest of each branch already read: one that runs
 * or waits first (the first in the branch order), else the one that moved
 * last (`updatedAt`, else `startedAt`), else the first in the branch order.
 * Undefined while not read, when the read failed, and without any.
 */
export function latestProjectPipeline(
  pipelines: ProjectPipelines | undefined,
): { branch: string; info: PipelineInfo } | undefined {
  if (pipelines?.status !== 'ready') {
    return undefined;
  }
  const entries = Object.entries(pipelines.byBranch)
    .sort(([a], [b]) => compareBranches(a, b))
    .map(([branch, info]) => ({ branch, info }));
  const running = entries.find(entry => isPipelineRunning(entry.info.status));
  if (running) {
    return running;
  }
  let latest = entries[0];
  for (const entry of entries) {
    const time = pipelineTime(entry.info);
    const best = latest ? pipelineTime(latest.info) : undefined;
    if (time !== undefined && (best === undefined || time > best)) {
      latest = entry;
    }
  }
  return latest;
}

/**
 * Runs `tasks` with at most `limit` of them in flight. Each task is expected
 * to turn its own failure into a value; a rejection is passed through.
 */
export async function runWithConcurrency(
  tasks: Array<() => Promise<void>>,
  limit: number,
): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) {
      const task = tasks[next];
      next += 1;
      await task();
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, tasks.length) }, worker),
  );
}
